import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
  useCallback,
} from 'react';
import './index.scss';
import { ScheduleStateEnum } from '@/utils/enum';
import { request } from '@/fetch/core';
import { Method } from '@/fetch/core/util';
import { HttpStatusCode } from '@/fetch/core/constant';
import StandardTileMap from '@/components/StandardTileMap';
import MapOperations from '@/utils/MapOperations';
import { isEmpty } from '@/utils/util';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';

export interface RealtimeMapEtaInfo {
  vehicleScheduleState?: string;
  estimatedArrivalTime?: string;
  nextMileage?: number;
}

// 实时地图组件，支持自定义路线规划
const RealtimeMap = forwardRef(
  (
    props: {
      vehicleName: string;
      zoom?: number;
      updateGoneRoute: number;
      etaInfo?: RealtimeMapEtaInfo | null;
    },
    ref
  ) => {
    const { vehicleName, zoom, updateGoneRoute, etaInfo } = props;

    const mapRef = useRef<any>(null); // 地图实例
    const mapOperateRef = useRef<MapOperations | null>(null); // 地图操作方法的类的实例
    const dragEndTimerRef = useRef<NodeJS.Timeout | null>(null); // 拖拽结束后的定时器
    const isDraggingRef = useRef<boolean>(false); // 是否正在拖拽地图
    const mapContainerRef = useRef<HTMLDivElement | null>(null); // 地图容器DOM引用
    const mouseDownRef = useRef<boolean>(false); // 鼠标是否按下
    const mouseMoveRef = useRef<boolean>(false); // 鼠标是否移动
    const isMountedRef = useRef(true);
    const [isMapInitialized, setIsMapInitialized] = useState<boolean>(false); // 地图是否已初始化
    const [isMapOperateReady, setIsMapOperateReady] = useState<boolean>(false); // 地图操作实例是否已准备就绪
    const [isLocalView, setIsLocalView] = useState<boolean>(false); // false: 总览, true: 局部
    const finishedRoutingInfo = useRef<any>([]);

    // ----------useSelector取值区域----------
    const LC_BUTTON_PLUS = useSelector(
      (state: RootState) => state?.controlBtn?.LC_BUTTON_PLUS
    );
    const latitude = useSelector(
      (state: RootState) => state?.realtime?.realtimeData?.latitude
    );
    const longitude = useSelector(
      (state: RootState) => state?.realtime?.realtimeData?.longitude
    );
    const rotation = useSelector(
      (state: RootState) => state?.realtime?.realtimeData?.realHeading
    );
    const scheduleState: ScheduleStateEnum = useSelector(
      (state: RootState) => state?.realtime?.realtimeData?.scheduleState
    );
    // ----------useSelector取值区域----------

    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
        // Ensure map operations and resources are fully cleaned up
        if (mapOperateRef.current) {
          mapOperateRef.current.clearAll();
          mapOperateRef.current = null;
        }
        if (mapRef.current) {
          mapRef.current.setTarget(null);
          mapRef.current.dispose?.();
          mapRef.current = null;
        }
      };
    }, []);
    useEffect(() => {
      if (!isMapInitialized) {
        return;
      }
      mapOperateRef.current = new MapOperations(mapRef.current);
      if (mapContainerRef.current) {
        const mapContainer = mapContainerRef.current;
        mapContainer.addEventListener('mousedown', handleMouseDown);
        mapContainer.addEventListener('mousemove', handleMouseMove);
        mapContainer.addEventListener('mouseup', handleMouseUp);
        mapContainer.addEventListener('mouseleave', handleMouseUp);
      }
      setIsMapOperateReady(true);

      return () => {
        if (mapContainerRef.current) {
          const mapContainer = mapContainerRef.current;
          mapContainer.removeEventListener('mousedown', handleMouseDown);
          mapContainer.removeEventListener('mousemove', handleMouseMove);
          mapContainer.removeEventListener('mouseup', handleMouseUp);
          mapContainer.removeEventListener('mouseleave', handleMouseUp);
        }
        if (dragEndTimerRef.current) {
          clearTimeout(dragEndTimerRef.current);
          dragEndTimerRef.current = null;
        }
      };
    }, [isMapInitialized]);

    useEffect(() => {
      if (!mapOperateRef.current) {
        return;
      }
      getSchedulePath();
      return () => {
        mapOperateRef.current?.clearVehicleInfoOverlay();
      };
    }, [scheduleState, isMapOperateReady, updateGoneRoute]);

    useEffect(() => {
      if (
        isMapOperateReady &&
        (scheduleState === 'SETOUT' || scheduleState === 'RETURN')
      ) {
        finishedRoutingInfo.current &&
          finishedRoutingInfo.current.push({ lat: latitude, lon: longitude });
        finishedRoutingPoint(finishedRoutingInfo.current, false);
      }
    }, [latitude, longitude, isMapOperateReady, scheduleState]);

    useEffect(() => {
      if (mapOperateRef.current) {
        mapOperateRef.current.updateRealTimePositionWithInfo(
          [longitude, latitude],
          rotation,
          etaInfo || undefined
        );
        if (!etaInfo) {
          mapOperateRef.current.clearVehicleInfoOverlay();
        }
        !isDraggingRef.current &&
          mapOperateRef.current.changeCenter([longitude, latitude]);
      }
    }, [
      latitude,
      longitude,
      rotation,
      isMapOperateReady,
      isDraggingRef.current,
      etaInfo?.vehicleScheduleState,
      etaInfo?.estimatedArrivalTime,
      etaInfo?.nextMileage,
    ]);

    useEffect(() => {
      LC_BUTTON_PLUS && handleToggleView();
    }, [LC_BUTTON_PLUS]);

    useImperativeHandle(ref, () => ({
      customRouting: (path: number[][]) => {
        if (mapOperateRef.current && path && path.length > 0) {
          mapOperateRef.current.customRouting(path);
        }
      },
    }));
    const handleMouseDown = () => {
      mouseDownRef.current = true;
      mouseMoveRef.current = false;
    };
    const handleMouseMove = () => {
      if (mouseDownRef.current) {
        mouseMoveRef.current = true;
        if (!isDraggingRef.current) {
          isDraggingRef.current = true;
          if (dragEndTimerRef.current) {
            clearTimeout(dragEndTimerRef.current);
            dragEndTimerRef.current = null;
          }
        }
      }
    };
    const handleMouseUp = () => {
      if (
        mouseDownRef.current &&
        mouseMoveRef.current &&
        isDraggingRef.current
      ) {
        if (dragEndTimerRef.current) {
          clearTimeout(dragEndTimerRef.current);
        }
        dragEndTimerRef.current = setTimeout(() => {
          isDraggingRef.current = false;
          if (mapOperateRef.current) {
            mapOperateRef.current.changeCenter([longitude, latitude]);
          }
          dragEndTimerRef.current = null;
        }, 5000);
      }
      mouseDownRef.current = false;
      mouseMoveRef.current = false;
    };

    /**
     * 地图实例准备就绪后，保存到mapRef，并标记地图已初始化
     */
    const setMapReady = useCallback((ref: any) => {
      // 将地图实例保存到mapRef
      mapRef.current = ref;
      // 需要标记好地图初始化，触发一次更新
      setIsMapInitialized(true);
    }, []);

    const clearPlanningRoutingPath = () => {
      mapOperateRef.current?.clearScheduledPath();
    };
    const clearFinishedRoutingPath = () => {
      mapOperateRef.current?.clearFinishedPath();
    };
    const planningRoutingPath = (
      planningRoutingPoint: { lat: number; lon: number }[]
    ) => {
      clearPlanningRoutingPath();
      if (isEmpty(planningRoutingPoint) || planningRoutingPoint.length < 2) {
        return;
      }
      const pathArr: any[] = [];
      planningRoutingPoint?.forEach((i: { lat: number; lon: number }) => {
        pathArr.push({ latitude: i.lat, longitude: i.lon });
      });
      mapOperateRef.current?.drawScheduledPath(pathArr);
    };
    const finishedRoutingPoint = (
      finishedRoutingPoint: { lat: number; lon: number }[],
      clear: boolean
    ) => {
      clear && clearFinishedRoutingPath();
      if (isEmpty(finishedRoutingPoint) || finishedRoutingPoint.length < 2) {
        return;
      }
      const pathArr: any[] = [];
      finishedRoutingPoint?.forEach((i: { lat: number; lon: number }) => {
        pathArr.push({ latitude: i.lat, longitude: i.lon });
      });
      mapOperateRef.current?.drawFinishedPath(pathArr);
    };
    const getSchedulePath = () => {
      request({
        path: '/monitor/web/map/vehicle',
        method: Method.POST,
        body: {
          vehicleName,
        },
      }).then((res) => {
        if (!isMountedRef.current) return;
        if (res.code === HttpStatusCode.Success) {
          planningRoutingPath(res?.data?.currentRoutingPoint);
          finishedRoutingInfo.current =
            res?.data?.currentFinishedRoutingPoint || [];
          finishedRoutingPoint(res?.data?.currentFinishedRoutingPoint, true);
          mapOperateRef.current?.drawStopOrHomePoints(
            res?.data?.currentStop || []
          );
        }
      });
    };

    const handleToggleView = () => {
      if (!mapOperateRef.current) {
        return;
      }
      const newIsLocal = !isLocalView;
      setIsLocalView(newIsLocal);
      if (newIsLocal) {
        mapOperateRef.current.setZoom(22);
      } else {
        mapOperateRef.current.setZoom(15);
      }
      mapOperateRef.current.changeCenter([longitude, latitude]);
    };

    return (
      <>
        <div className="toggle-local-all-btn" onClick={handleToggleView}>
          <span>{isLocalView ? '切换总览' : '切换局部'}</span>
        </div>

        <StandardTileMap
          latitude={latitude}
          longitude={longitude}
          zoom={zoom}
          forceAllHighPrecisionMapVisible={isLocalView}
          getMapRef={setMapReady}
          getMapContainerRef={(container) =>
            (mapContainerRef.current = container)
          }
        />
      </>
    );
  }
);

// 添加displayName
RealtimeMap.displayName = 'RealtimeMap';

export default React.memo(RealtimeMap);
